import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, CheckCircle2, ShieldCheck, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { track } from "@/lib/analytics";
import { ENQ } from "@/constants/testIds";
import { useT, useLang } from "@/lib/i18n";
import {
  msg91Enabled,
  loadMsg91,
  toMsisdn,
  sendOtp,
  retryOtp,
  verifyOtp,
  resetMsg91Token,
  msg91ErrorMessage,
} from "@/lib/msg91";

const PROGRAMMES = ["Tool & Die Making", "Mechatronics", "Precision Machinist", "Not sure yet"];
const QUALIFICATIONS = ["10th Pass", "12th Pass", "Diploma", "Graduate", "Other"];
const PROGRAMMES_HI = ["टूल एंड डाई मेकिंग", "मेक्ट्रोनिक्स", "प्रिसीज़न मशीनिस्ट", "अभी तय नहीं"];
const QUALIFICATIONS_HI = ["10वीं पास", "12वीं पास", "डिप्लोमा", "स्नातक", "अन्य"];
const WA_CHANNEL = "12";  // MSG91 retry channel code for WhatsApp

export function EnquiryModal({ open, onClose }) {
  const t = useT();
  const navigate = useNavigate();
  const { lang } = useLang();
  const isHi = lang === "hi";
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    programme: "Tool & Die Making",
    qualification: "12th Pass",
    city: "",
    message: "",
  });
  const [otp, setOtp] = useState("");
  const [reqId, setReqId] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [otpRequired, setOtpRequired] = useState(false);

  useEffect(() => {
    let active = true;
    api.get("/settings")
      .then(({ data }) => { if (active) setOtpRequired(!!data.otp_required); })
      .catch(() => {});
    // Warm up the MSG91 widget so verification is instant when the user submits.
    if (open && msg91Enabled) loadMsg91().catch(() => {});
    return () => { active = false; };
  }, [open]);

  useEffect(() => {
    if (!open) {
      const timer = setTimeout(() => {
        setStep(1);
        setError("");
        setOtp("");
        setReqId("");
      }, 500);
      return () => clearTimeout(timer);
    }
    document.body.style.overflow = "hidden";
    track("enquiry_modal_open");
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((v) => v - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submitStep1 = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return setError(t("enq.errEmail"));
    if (form.phone.replace(/\D/g, "").length < 8) return setError(t("enq.errPhone"));
    if (form.name.trim().length < 2) return setError(t("enq.errName"));
    if (form.city.trim().length < 2) return setError(t("enq.errCity"));
    setLoading(true);
    try {
      if (!otpRequired) {
        await api.post("/enquiry/direct", {
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          programme: form.programme,
          qualification: form.qualification,
          city: form.city.trim(),
          message: (form.message || "").trim(),
        });
        track("enquiry_submitted", { programme: form.programme, otp: false });
        onClose();
        navigate("/thank-you");
        return;
      }
      if (!msg91Enabled) throw new Error("Phone verification is temporarily unavailable. Please try again shortly.");
      await loadMsg91();
      resetMsg91Token();
      const id = await sendOtp(toMsisdn(form.phone.trim()));
      setReqId(id);
      setStep(2);
      setResendCooldown(30);
      track("otp_sent", { channel: "whatsapp" });
      toast.success(t("enq.codeSent"), { description: t("enq.codeSentWa", { phone: `+${toMsisdn(form.phone.trim())}` }) });
    } catch (err) {
      setError(msg91ErrorMessage(err) || formatApiError(err?.response?.data?.detail) || "Couldn't submit right now. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const submitStep2 = async (e) => {
    e.preventDefault();
    setError("");
    if (otp.replace(/\D/g, "").length < 4) return setError(t("enq.errOtp"));
    setLoading(true);
    try {
      const accessToken = await verifyOtp(otp.trim(), reqId);
      await api.post("/enquiry/msg91", {
        access_token: accessToken,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        programme: form.programme,
        qualification: form.qualification,
        city: form.city.trim(),
        message: (form.message || "").trim(),
      });
      track("enquiry_verified", { programme: form.programme });
      onClose();
      navigate("/thank-you");
    } catch (err) {
      setError(msg91ErrorMessage(err) || formatApiError(err?.response?.data?.detail) || "Verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const resend = async (channel = WA_CHANNEL) => {
    if (resendCooldown > 0 || loading) return;
    setLoading(true);
    setError("");
    try {
      await retryOtp(channel, reqId);
      setResendCooldown(30);
      toast.success(channel === WA_CHANNEL ? t("enq.codeSentWaToast") : t("enq.codeResent"));
    } catch (err) {
      setError(msg91ErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const progLabels = isHi ? PROGRAMMES_HI : PROGRAMMES;
  const qualLabels = isHi ? QUALIFICATIONS_HI : QUALIFICATIONS;
  const hindiCls = isHi ? "font-hindi" : "";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid={ENQ.modal}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6"
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            initial={{ y: 24, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 24, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative bg-background text-foreground w-full max-w-xl max-h-[94vh] overflow-y-auto border border-foreground shadow-[8px_8px_0_0_hsl(var(--foreground))]"
          >
            <button data-testid={ENQ.close} onClick={onClose} className="absolute top-4 right-4 z-10 w-9 h-9 grid place-items-center rounded-full border border-border bg-background/80 hover:border-foreground transition-colors" aria-label={t("enq.close")}>
              <X className="w-4 h-4" strokeWidth={1.8} />
            </button>

            <div className="px-6 md:px-8 py-6 md:py-7">
              {otpRequired && step < 3 && (
                <div className="mb-4 flex items-center gap-3">
                  <div className={`h-px flex-1 ${step >= 1 ? "bg-foreground" : "bg-border"}`} />
                  <span className="label-overline">{step} / 2</span>
                  <div className={`h-px flex-1 ${step >= 2 ? "bg-foreground" : "bg-border"}`} />
                </div>
              )}

              {step === 1 && (
                <form onSubmit={submitStep1} data-testid={ENQ.step1Form} className="space-y-4">
                  <div className="pr-10">
                    <h3 className={`font-bold text-xl md:text-2xl leading-tight tracking-tight ${isHi ? "font-hindi" : "font-sans"}`}>{t("enq.step1Title")}</h3>
                    {otpRequired && <p className={`text-muted-foreground mt-1.5 text-sm ${hindiCls}`}>{t("enq.step1Sub")}</p>}
                  </div>

                  <Field label={t("enq.fName")} isHi={isHi}>
                    <input data-testid={ENQ.inputName} value={form.name} onChange={update("name")} placeholder={t("enq.fNamePh")} className={`kiki-input ${hindiCls}`} />
                  </Field>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label={t("enq.fEmail")} isHi={isHi}>
                      <input data-testid={ENQ.inputEmail} type="email" value={form.email} onChange={update("email")} placeholder={t("enq.fEmailPh")} className="kiki-input" />
                    </Field>
                    <Field label={t("enq.fPhone")} isHi={isHi}>
                      <input data-testid={ENQ.inputPhone} value={form.phone} onChange={update("phone")} placeholder={t("enq.fPhonePh")} className="kiki-input" />
                    </Field>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label={t("enq.fProgramme")} isHi={isHi}>
                      <select data-testid={ENQ.inputProgramme} value={form.programme} onChange={update("programme")} className={`kiki-input ${hindiCls}`}>
                        {PROGRAMMES.map((p, i) => <option key={p} value={p}>{progLabels[i]}</option>)}
                      </select>
                    </Field>
                    <Field label={t("enq.fQualification")} isHi={isHi}>
                      <select data-testid={ENQ.inputQualification} value={form.qualification} onChange={update("qualification")} className={`kiki-input ${hindiCls}`}>
                        {QUALIFICATIONS.map((p, i) => <option key={p} value={p}>{qualLabels[i]}</option>)}
                      </select>
                    </Field>
                  </div>
                  <Field label={t("enq.fCity")} isHi={isHi}>
                    <input data-testid={ENQ.inputCity} value={form.city} onChange={update("city")} placeholder={t("enq.fCityPh")} className={`kiki-input ${hindiCls}`} />
                  </Field>
                  <Field label={t("enq.fMessage")} isHi={isHi}>
                    <textarea data-testid={ENQ.inputMessage} rows={2} value={form.message} onChange={update("message")} placeholder={t("enq.fMessagePh")} className={`kiki-input resize-none ${hindiCls}`} />
                  </Field>

                  {error && <div className={`text-sm text-destructive font-mono ${hindiCls}`}>{error}</div>}

                  <button data-testid={ENQ.sendOtpBtn} disabled={loading} type="submit" className={`btn-pill-yellow w-full py-3.5 ${hindiCls}`}>
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (otpRequired ? t("enq.sendOtp") : t("enq.submitEnquiry"))}
                  </button>
                  {otpRequired && (
                    <p className={`label-overline text-muted-foreground text-center pt-2 ${isHi ? "font-hindi tracking-normal text-[12px]" : ""}`}>
                      <ShieldCheck className="inline w-3 h-3 mr-1.5 -mt-0.5" /> {t("enq.otpBadge")}
                    </p>
                  )}
                </form>
              )}

              {step === 2 && (
                <form onSubmit={submitStep2} data-testid={ENQ.step2Form} className="space-y-6">
                  <div>
                    <h3 className={`font-black text-4xl md:text-5xl leading-[1] tracking-tight ${isHi ? "font-hindi" : "font-sans"}`}>{t("enq.step2Title")}</h3>
                    <p className={`text-muted-foreground mt-3 text-sm ${hindiCls}`}>
                      {t("enq.step2SubWa", { phone: form.phone })}
                    </p>
                  </div>

                  <Field label={t("enq.fOtp")} isHi={isHi}>
                    <input
                      data-testid={ENQ.inputOtp}
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="•  •  •  •  •  •"
                      inputMode="numeric"
                      className="kiki-input font-mono text-2xl tracking-[0.7em] text-center py-4"
                    />
                  </Field>

                  {error && <div className={`text-sm text-destructive font-mono ${hindiCls}`}>{error}</div>}

                  <button data-testid={ENQ.verifyBtn} disabled={loading} type="submit" className={`btn-pill-yellow w-full py-4 ${hindiCls}`}>
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("enq.verify")}
                  </button>

                  <div className="flex items-center justify-between text-xs">
                    <button
                      type="button"
                      data-testid={ENQ.resendBtn}
                      onClick={() => resend(WA_CHANNEL)}
                      disabled={resendCooldown > 0 || loading}
                      className={`inline-flex items-center gap-2 text-[#128C4A] hover:text-[#25D366] disabled:opacity-40 transition-colors ${isHi ? "font-hindi text-[12px]" : "label-overline tracking-normal"}`}
                    >
                      <MessageCircle className="w-3.5 h-3.5" strokeWidth={2} /> {t("enq.resendWa")}{resendCooldown > 0 ? ` · ${resendCooldown}s` : ""}
                    </button>
                    <button type="button" onClick={() => setStep(1)} className={`label-overline text-muted-foreground hover:text-foreground link-sweep ${isHi ? "font-hindi tracking-normal text-[12px]" : ""}`}>
                      {t("enq.change")}
                    </button>
                  </div>
                </form>
              )}

              {step === 3 && (
                <div data-testid={ENQ.successView} className="text-center py-8">
                  <motion.div
                    initial={{ scale: 0.6, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    className="w-20 h-20 mx-auto grid place-items-center bg-[#FFCC00] text-[#050505]"
                  >
                    <CheckCircle2 className="w-9 h-9" strokeWidth={1.4} />
                  </motion.div>
                  <h3 className={`font-black text-4xl md:text-5xl leading-[1] tracking-tight mt-8 ${isHi ? "font-hindi" : "font-sans"}`}>{t("enq.successTitle")}<br />{t("enq.successTitle2")}</h3>
                  <p className={`text-muted-foreground mt-5 max-w-sm mx-auto ${hindiCls}`}>
                    {t("enq.successBody")}
                  </p>
                  <div className="mt-8 grid grid-cols-3 gap-3 text-left">
                    <Info label={t("enq.infoProgramme")} value={form.programme} isHi={isHi} />
                    <Info label={t("enq.infoPhone")} value={form.phone} isHi={isHi} />
                    <Info label={t("enq.infoCity")} value={form.city} isHi={isHi} />
                  </div>
                  <button data-testid={ENQ.successClose} onClick={onClose} className={`btn-pill-yellow mt-10 px-8 ${hindiCls}`}>{t("enq.close")}</button>
                </div>
              )}
            </div>
          </motion.div>

          <style>{`
            .kiki-input {
              width: 100%;
              padding: 12px 14px;
              background: transparent;
              border: 1px solid hsl(var(--border));
              border-radius: 0;
              color: hsl(var(--foreground));
              font-size: 15px;
              font-family: inherit;
              transition: border-color 0.3s ease;
              outline: none;
            }
            .kiki-input:focus { border-color: hsl(var(--foreground)); }
            .kiki-input::placeholder { color: hsl(var(--muted-foreground)); opacity: 0.6; }
          `}</style>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Field({ label, children, isHi }) {
  return (
    <label className="block">
      <span className={`label-overline text-muted-foreground block mb-2 ${isHi ? "font-hindi tracking-normal text-[12px]" : ""}`}>{label}</span>
      {children}
    </label>
  );
}

function Info({ label, value, isHi }) {
  return (
    <div className="border border-border p-3">
      <div className={`label-overline text-muted-foreground text-[10px] ${isHi ? "font-hindi tracking-normal text-[11px]" : ""}`}>{label}</div>
      <div className={`text-sm truncate mt-1 ${isHi ? "font-hindi" : ""}`}>{value || "—"}</div>
    </div>
  );
}
